import type { TourId } from '@/features/tour/types.js';

/**
 * Whether a customer has seen a tour (MYP-83), kept in the browser.
 *
 * The vendor API has nowhere to keep a preference on the account, so it lives here — which means
 * a tour shows again on a new device or after site data is cleared. The ticket accepts that.
 *
 * Keyed by tour and customer ID, so a second account signed in on the same browser still gets its
 * tours. `v1` is there so a rewritten tour can be shown again by moving to `v2`. The Create order
 * tour's key (`mypoz_tour_create_order:v1:…`) predates the others and is kept as it was, so no one
 * who has already seen it sees it again.
 *
 * Every read and write is guarded: storage can be switched off, full or throwing in a private
 * window. A failed read counts as "seen" — a tour that cannot remember being dismissed would come
 * back on every visit, which is worse than not coming at all — and a failed write is ignored.
 */
const key = (tour: TourId, customerId: string) => `mypoz_tour_${tour}:v1:${customerId}`;

export function hasSeenTour(tour: TourId, customerId: string): boolean {
  try {
    return localStorage.getItem(key(tour, customerId)) !== null;
  } catch {
    return true;
  }
}

export function markTourSeen(tour: TourId, customerId: string): void {
  try {
    localStorage.setItem(key(tour, customerId), new Date().toISOString());
  } catch {
    // Nothing to do: the tour simply may show again next time.
  }
}
